import { drawerAnatomy } from '@chakra-ui/react/anatomy';
import { defineSlotRecipe } from '@chakra-ui/react/styled-system';

import { rem } from '../themeUtils';

const drawerSlotRecipe = defineSlotRecipe({
  className: 'drawer',
  slots: drawerAnatomy.keys(),
  base: {
    backdrop: {
      background: 'utilities/overlay-light',
      inset: 0,
      position: 'fixed',
      zIndex: 'calc({zIndex.dialogOverlay} + var(--layer-index, 0))',
      _open: {
        animationName: 'fade-in',
        animationDuration: 'slow',
      },
      _closed: {
        animationName: 'fade-out',
        animationDuration: 'moderate',
      },
    },
    positioner: {
      alignItems: 'stretch',
      display: 'flex',
      inset: 0,
      justifyContent: 'flex-end',
      overscrollBehaviorY: 'none',
      position: 'fixed',
      zIndex: 'calc({zIndex.dialog} + var(--layer-index, 0))',
    },
    content: {
      background: 'background/primary',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '100dvh',
      outline: 'none',
      position: 'relative',
      _open: {
        animationDuration: 'slowest',
        animationName: {
          base: 'slide-from-right-full, fade-in',
          _rtl: 'slide-from-left-full, fade-in',
        },
        animationTimingFunction: 'ease-in-smooth',
      },
      _closed: {
        animationDuration: 'slower',
        animationName: {
          base: 'slide-to-right-full, fade-out',
          _rtl: 'slide-to-left-full, fade-out',
        },
        animationTimingFunction: 'ease-in-smooth',
      },
    },
    header: {
      paddingBlockEnd: '8',
      paddingBlockStart: '24',
      paddingInline: '24',
      // A tab list in the header closes it off with its own baseline.
      '&:has([role=tablist])': {
        paddingBlockEnd: 0,
      },
    },
    body: {
      flex: '1',
      overflow: 'auto',
    },
    footer: {
      borderBlockStartColor: 'border/regular',
      borderBlockStartWidth: '1',
      paddingBlock: '12',
    },
    title: {
      color: 'text/tertiary',
      flex: '1',
      // The close button is absolutely positioned, so the title keeps clear of it.
      paddingInlineEnd: '48',
      textStyle: 'heading/h6',
    },
    description: {},
    closeTrigger: {
      alignItems: 'center',
      borderRadius: '4',
      color: 'icon/primary',
      cursor: 'pointer',
      display: 'inline-flex',
      height: '40',
      justifyContent: 'center',
      position: 'absolute',
      width: '40',
      _hover: {
        backgroundColor: 'color/neutral/subtle',
      },
      _active: {
        backgroundColor: 'color/neutral/moderate',
      },
    },
  },
  variants: {
    variant: {
      // One inline padding for header, body and footer, so their content lines up: 16 on mobile, 24 from tablet.
      docked: {
        body: {
          padding: { base: '16', tablet: '24' },
        },
        closeTrigger: {
          insetEnd: rem(18),
          top: '12',
        },
        content: {
          width: { base: '100vw', tablet: rem(320) },
        },
        footer: {
          paddingInline: { base: '16', tablet: '24' },
        },
        header: {
          paddingInline: { base: '16', tablet: '24' },
          // Tabs sit 8px from the edge, so their baseline runs nearly full width.
          '& :has(> [role=tablist])': {
            marginInline: { base: '-8', tablet: '-16' },
          },
        },
      },
      floating: {
        body: {
          paddingBlockStart: '8',
          paddingInline: '24',
          '.chakra-drawer__header:has([role=tablist]) ~ &': {
            paddingBlockStart: '24',
          },
        },
        closeTrigger: {
          height: '32',
          insetEnd: '20',
          top: '20',
          width: '32',
        },
        positioner: {
          padding: '32',
        },
        content: {
          borderRadius: '12',
          boxShadow: 'elevation/lg',
          maxWidth: rem(700),
          // 480 is the floating drawer's smallest step; on narrow screens it gives way to the viewport.
          minWidth: `min(100%, ${rem(480)})`,
        },
        header: {
          paddingBlockEnd: '16',
          paddingBlockStart: '24',
          paddingInline: '24',
          '& :has(> [role=tablist])': {
            marginInline: '-16',
          },
        },
        footer: {
          borderBlockStartColor: 'transparent',
          borderBlockStartWidth: 0,
          paddingBlockEnd: '24',
          paddingBlockStart: '32',
          paddingInline: '24',
        },
        title: {
          color: 'text/primary',
          textStyle: 'heading/h3',
        },
      },
    },
    // Fixed widths from tablet up; below that the docked drawer is full width. The floating drawer starts at sm
    // (480) and, without a size, sizes to its content between 480 and 700.
    size: {
      xxs: {},
      xs: {},
      sm: {},
      md: {},
      lg: {},
    },
  },
  compoundVariants: [
    ...(
      [
        ['xxs', 320],
        ['xs', 360],
        ['sm', 480],
        ['md', 640],
        ['lg', 800],
      ] as const
    ).map(([size, width]) => ({
      css: { content: { width: { base: '100vw', tablet: rem(width) } } },
      size,
      variant: 'docked' as const,
    })),
    // The floating drawer's scale starts at 480, so the docked-only xxs and xs steps map to it there.
    ...(
      [
        ['xxs', 480],
        ['xs', 480],
        ['sm', 480],
        ['md', 640],
        ['lg', 800],
      ] as const
    ).map(([size, width]) => ({
      css: { content: { maxWidth: '100%', width: rem(width) } },
      size,
      variant: 'floating' as const,
    })),
  ],
  defaultVariants: {
    variant: 'docked',
  },
});

export default drawerSlotRecipe;
